// Toast queue reducers — pure, so the flood behaviour is testable without
// mounting AppShell or faking a WebSocket.
//
// The bug these exist to fix: `addToast` appended one toast per
// `alert.triggered` frame with no cap and no coalescing, and `Toast.jsx`
// rendered every entry in a flex column. During ordinary `device_down` churn
// — and catastrophically during a storm (#814 fired 853 criticals) — the queue
// filled the viewport with near-identical cards that scrolled past and vanished
// on a 5s timer. Unreadable while it happened, and no record afterwards, so the
// operator got neither a notification nor a log.
//
// Toasts are for *notification*, not for carrying a burst. The alert list is
// the durable surface; this queue's job is to say "something is happening" and
// stay out of the way. Hence: coalesce duplicates, cap what's visible, and
// report the overflow as a count rather than as more cards.

/** Most toasts rendered at once. Beyond this, the overflow is summarised. */
export const MAX_VISIBLE_TOASTS = 4;

/**
 * Add a toast, coalescing against an identical one already queued.
 *
 * "Identical" is (message, severity) — a flapping device re-fires the same
 * rendered message, so the useful signal is "this happened 37 times", not 37
 * separate cards. The coalesced entry's `id` is preserved (React keeps the
 * same DOM node) while `count` increments and `seq` moves to the front of the
 * queue so it reads as fresh.
 *
 * `nextId` is passed in rather than derived from a clock. `Date.now()` was the
 * previous id source and it collided: alerts arrive in bursts, two in the same
 * millisecond shared an id, which produced duplicate React keys AND made the
 * first expiry sweep remove *every* toast sharing that id — so toasts
 * disappeared early and unpredictably.
 *
 * Returns a NEW array; never mutates `toasts`.
 */
export function addToast(toasts, { message, severity = 'info', id, seq }) {
  const existing = toasts.findIndex(
    (t) => t.message === message && t.severity === severity,
  );

  if (existing !== -1) {
    const next = toasts.slice();
    next[existing] = {
      ...next[existing],
      count: next[existing].count + 1,
      seq,
    };
    return next;
  }

  return [...toasts, { id, message, severity, count: 1, seq }];
}

/**
 * Split the queue into what to render and how much is hidden.
 *
 * Newest-first by `seq`, so a burst does not bury the most recent event under
 * older cards. The overflow is returned as a count for the caller to summarise
 * — rendering it would recreate the flood.
 */
export function visibleToasts(toasts, max = MAX_VISIBLE_TOASTS) {
  const ordered = toasts.slice().sort((a, b) => b.seq - a.seq);
  return {
    visible: ordered.slice(0, max),
    hiddenCount: Math.max(0, ordered.length - max),
  };
}

/**
 * Remove one toast by id.
 *
 * Ids are unique per queue entry (see `addToast`), so this removes exactly one
 * — unlike the `Date.now()` scheme, where a shared id removed every colliding
 * entry at once.
 */
export function removeToast(toasts, id) {
  return toasts.filter((t) => t.id !== id);
}

/**
 * Drop toasts whose lifetime has elapsed.
 *
 * Expiry is computed from `seq` (a monotonic millisecond stamp) rather than
 * scheduled per-toast, so a coalesced toast's clock restarts when it is hit
 * again: a device flapping every 2s keeps ONE card alive instead of leaving a
 * trail of expiring ones. It also means no timer leaks per event, which
 * mattered at burst volume — the old code scheduled a `setTimeout` per frame.
 */
export function expireToasts(toasts, now, ttlMs) {
  return toasts.filter((t) => now - t.seq < ttlMs);
}
